/**
 * Online verseny-szinkron.
 *
 * Modell: host-authoritative. A gazda futtatja a World-öt, a kliensek a saját
 * bemenetüket küldik, és a gazda 20 Hz-en küld pillanatképet. A kliens a saját
 * kocsiját lokálisan prediktálja (azonnali kormányzás), a többieket ~120 ms-os
 * interpolációs pufferrel simítja. A lebegőpontos eltérés nem gond: a host az
 * igazság, a kliens csak korrigál.
 */
import type { Hazard, ItemEvent, ItemId, Projectile, RaceResult, Vec2 } from '../core/types';
import { IDLE_INPUT } from '../core/types';
import type { World } from '../game/world';
import { Kart } from '../game/kart';
import type { OnlineClient } from './client';
import type { BumpWire, InputWire, ItemEventWire, KartSnap, OnlineMatchInfo, Snapshot } from './protocol';
import { INPUT_HZ, INTERP_DELAY, SNAPSHOT_HZ } from './protocol';

export interface OnlineRaceHandlers {
  onEvents?: (events: ItemEvent[]) => void;
  onBumps?: (bumps: { at: Vec2; strength: number }[]) => void;
  onResult?: (result: RaceResult) => void;
  onLeft?: (slot: number, name: string) => void;
  onClosed?: (message: string) => void;
  /** a gazda fül hátérbe került / visszatért (a verseny szünetel) */
  onHostPause?: (paused: boolean) => void;
  /** a fül hosszabb háttér után tért vissza (mp) */
  onResume?: (seconds: number) => void;
}

interface SnapshotEntry {
  at: number;
  data: Snapshot;
}

const SNAP_MAX = 30;
const REMOTE_TIMEOUT = 0.75;
const HARD_SNAP = 260;
/** ennél tovább ne engedjük előre a renderelt órát a legfrissebb snapshotnál */
const MAX_EXTRAPOLATION = 0.15;
const MAX_INPUT_BACKLOG = 1 / INPUT_HZ;
const PREDICTION_KEY = 'csf.online.prediction';

const readPrediction = (): boolean => {
  try {
    // alapból KI: a saját kocsi is telemetriából interpolál (sima menet)
    return localStorage.getItem(PREDICTION_KEY) === '1';
  } catch {
    return false;
  }
};

export class OnlineRaceSync {
  readonly host: boolean;
  readonly localIndex: number;
  readonly match: OnlineMatchInfo;

  private readonly client: OnlineClient;
  private readonly handlers: OnlineRaceHandlers;
  private snapshots: SnapshotEntry[] = [];
  private pred: Kart | null = null;
  private corr = { x: 0, y: 0, h: 0 };
  private readonly remoteInput = new Map<number, { input: InputWire; at: number }>();
  private pendingBumps: BumpWire[] = [];
  private lastInputSent = 0;
  private lastSnapshotSent = 0;
  private finishedSent = false;
  private resultReceived = false;
  private closed = false;
  private hiddenAt = 0;
  /** a gazda fülét háttérbe tették: ne prediktáljunk tovább */
  private hostPaused = false;
  /** nem jött snapshot: a gazda szimulációja áll */
  private stalled = false;
  private lastSnapshotAt = 0;
  /** szünet után ennyi ideig ne álljon meg újra a snapshot-ingadozás miatt */
  private stallGraceUntil = 0;
  /** a renderelt óra soha ne menjen visszafelé (nincs hátraugrálás) */
  private lastTargetTime = 0;
  /** a saját kocsi vizuális simítása (a fizikát nem érinti) */
  private readonly visual = { x: 0, y: 0, h: 0, ready: false };
  /** saját kocsi predikciója; kikapcsolva minden kocsi telemetriából interpolál */
  private predictionEnabled = readPrediction();
  private readonly cleanups: (() => void)[] = [];
  /** élre triggerelt gombok: a 30 Hz-es küldés ne veszítse el a lenyomást */
  private pendingUse = false;
  private pendingAbility = false;

  constructor(client: OnlineClient, match: OnlineMatchInfo, handlers: OnlineRaceHandlers = {}) {
    this.client = client;
    this.match = match;
    this.handlers = handlers;
    this.host = match.you.host;
    this.localIndex = Math.max(0, match.humans.findIndex((h) => h.id === match.you.id));

    this.cleanups.push(
      client.on('input', (msg) => {
        if (!this.host) return;
        const index = this.match.humans.findIndex((h) => h.slot === msg.slot);
        if (index < 0) return;
        this.remoteInput.set(index, { input: msg.input, at: this.now() });
      }),
    );

    this.cleanups.push(
      client.on('events', (msg) => {
        if (this.host || !msg.e.length) return;
        const events = msg.e.map(fromWireEvent);
        this.handlers.onEvents?.(events);
      }),
    );

    this.cleanups.push(
      client.on('pause', (msg) => {
        if (this.host) return;
        this.applyHostPause(msg.paused);
      }),
    );

    this.cleanups.push(
      client.on('snapshot', (msg) => {
        if (this.host) return;
        this.onSnapshot(msg.s);
      }),
    );

    this.cleanups.push(
      client.on('result', (msg) => {
        if (this.host) return;
        // a végeredmény után már ne írja felül a pillanatkép a fázist
        this.resultReceived = true;
        this.handlers.onResult?.(msg.result as RaceResult);
      }),
    );

    this.cleanups.push(
      client.on('left', (msg) => {
        if (!this.host) return;
        const human = this.match.humans.find((h) => h.id === msg.id);
        this.handlers.onLeft?.(msg.slot, human?.name ?? 'Játékos');
      }),
    );

    this.cleanups.push(
      client.on('closed', (msg) => {
        if (this.closed) return;
        this.closed = true;
        this.handlers.onClosed?.(msg.message);
      }),
    );

    if (typeof document !== 'undefined') {
      const onVisibility = (): void => this.onVisibility();
      document.addEventListener('visibilitychange', onVisibility);
      this.cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility));
    }
  }

  /** A verseny végén le kell iratkozni, különben egy új futamnál duplán szólna. */
  destroy(): void {
    for (const off of this.cleanups.splice(0)) off();
  }

  get predictionOn(): boolean {
    return this.predictionEnabled;
  }

  /**
   * Predikció be/ki. Kikapcsolva a saját kocsi is a szerverállapotból
   * interpolál (sima, de a bemenet késik); bekapcsolva azonnal reagál,
   * de a 20 Hz-es korrekciók miatt kicsit remeghet.
   */
  togglePrediction(): boolean {
    this.predictionEnabled = !this.predictionEnabled;
    try {
      localStorage.setItem(PREDICTION_KEY, this.predictionEnabled ? '1' : '0');
    } catch {
      /* privát mód */
    }
    this.pred = null;
    this.corr = { x: 0, y: 0, h: 0 };
    this.visual.ready = false;
    return this.predictionEnabled;
  }

  /**
   * Háttérbe tett fül: a rAF megáll, így nem megy bemenet, a kocsi a host
   * szerint ott marad, ahol volt. Visszatéréskor eldobjuk a régi predikciót és
   * a snapshot-puffert, hogy a friss szerverállapotból folytassuk – így nem
   * ugrik vissza a kocsi egy régi pozícióra.
   */
  private onVisibility(): void {
    if (document.hidden) {
      this.hiddenAt = this.now();
      this.pendingUse = false;
      this.pendingAbility = false;
      if (this.host) this.client.sendPause(true);
      else this.client.sendInput(IDLE_INPUT);
      return;
    }
    if (this.hiddenAt === 0) return;
    const away = this.now() - this.hiddenAt;
    this.hiddenAt = 0;
    // a szünetjelet akkor is le kell zárni, ha csak egy pillanatra váltottunk
    if (this.host) this.client.sendPause(false);
    if (away < 0.35) return;
    this.snapshots = [];
    this.pred = null;
    this.corr = { x: 0, y: 0, h: 0 };
    this.visual.ready = false;
    this.lastTargetTime = 0;
    if (!this.host) this.lastSnapshotAt = 0;
    this.handlers.onResume?.(away);
  }

  /** A gazda fül szünetjelzése (tab háttérbe került / visszatért). */
  private applyHostPause(paused: boolean): void {
    if (this.host) return;
    if (paused) {
      if (this.hostPaused || this.stalled) return;
      this.hostPaused = true;
      this.handlers.onHostPause?.(true);
      return;
    }
    if (!this.hostPaused && !this.stalled) return;
    this.hostPaused = false;
    this.stalled = false;
    this.lastSnapshotAt = 0;
    this.lastTargetTime = 0;
    this.stallGraceUntil = this.now() + 1.0;
    this.pred = null;
    this.snapshots = [];
    this.corr = { x: 0, y: 0, h: 0 };
    this.visual.ready = false;
    this.handlers.onHostPause?.(false);
  }

  /** 1,5 mp-ig nem jött snapshot: a gazda szimulációja áll, álljunk meg. */
  private markStall(world: World): void {
    if (this.stalled) return;
    this.stalled = true;
    // az óra se fusson tovább a gazda szünete alatt
    const latest = this.snapshots[this.snapshots.length - 1];
    if (latest) world.time = latest.data.tm;
    this.lastTargetTime = 0;
    this.handlers.onHostPause?.(true);
  }

  /** A gazda a saját helyi bemenetét is beállítja, majd a távoli slotokat. */
  hostApplyInputs(world: World, localInput: import('../core/types').InputState): void {
    if (!this.host) return;
    const t = this.now();
    world.players.forEach((kart, index) => {
      if (index === this.localIndex) {
        kart.setInput(localInput);
        return;
      }
      const entry = this.remoteInput.get(index);
      if (!entry || t - entry.at > REMOTE_TIMEOUT) {
        kart.setInput(IDLE_INPUT);
        return;
      }
      kart.setInput(wireToInput(entry.input));
    });
  }

  /** A gazda a lépés után hívja: események azonnal, pillanatkép 20 Hz-en. */
  hostAfterStep(world: World, events: ItemEvent[], bumps: { at: Vec2; strength: number }[]): void {
    if (!this.host) return;
    if (events.length > 0) this.client.sendEvents(events.map(toWireEvent));
    this.pendingBumps.push(...bumps.map(toWireBump));
    const t = this.now();
    // a záró pillanatkép ne csússzon el a 20 Hz-es korláton
    if (world.phase !== 'finished' && t - this.lastSnapshotSent < 1 / SNAPSHOT_HZ) return;
    this.lastSnapshotSent = t;
    this.client.sendSnapshot(buildSnapshot(world, this.pendingBumps));
    this.pendingBumps = [];
  }

  /** A gazda a verseny végén egyszer küldi el az eredményt. */
  hostFinish(result: RaceResult): void {
    if (!this.host || this.finishedSent) return;
    this.finishedSent = true;
    this.client.raceEnd(result);
  }

  /**
   * Kliens-oldali frissítés: interpoláció + saját kocsi predikciója.
   * @returns a renderelt versenyidő (mp)
   */
  guestUpdate(world: World, input: import('../core/types').InputState, dt: number): number {
    if (this.resultReceived) return world.time;
    if (this.hostPaused) return world.time;
    const t = this.now();
    // a gazda fül háttérben / a szimuláció megállt: ne prediktáljunk előre
    if (this.lastSnapshotAt > 0 && t - this.lastSnapshotAt > 1.5 && t > this.stallGraceUntil) {
      this.markStall(world);
      return world.time;
    }
    if (input.useItem) this.pendingUse = true;
    if (input.ability) this.pendingAbility = true;
    if (t - this.lastInputSent >= MAX_INPUT_BACKLOG) {
      this.lastInputSent = t;
      this.client.sendInput({
        ...input,
        useItem: this.pendingUse,
        ability: this.pendingAbility,
      });
      this.pendingUse = false;
      this.pendingAbility = false;
    }
    if (this.snapshots.length === 0) return world.time;

    const target = this.interpolate(world, t);
    const local = world.players[this.localIndex];
    const localSpeed = local ? Math.hypot(local.vel.x, local.vel.y) : 0;
    // álló helyzetben (vagy kikapcsolt predikciónál) ne prediktáljunk:
    // a kocsi a szerverállapotból interpolálódik, így nincs korrekciós remegés
    const noPrediction =
      !this.predictionEnabled ||
      (input.throttle < 0.02 &&
        input.brake < 0.02 &&
        Math.abs(input.steer) < 0.02 &&
        !input.drift &&
        localSpeed < 25);
    if (noPrediction) {
      this.pred = null;
      this.corr = { x: 0, y: 0, h: 0 };
      this.visual.ready = false;
      local?.setInput(input);
    } else {
      this.predictLocal(world, input, dt);
    }
    world.time = target;
    return target;
  }

  private onSnapshot(snapshot: Snapshot): void {
    const at = this.now();
    // a gazda explicit szünetjelzése után egy késve érkező pillanatkép még
    // nem jelenti azt, hogy folytatjuk – a pause:false-ra várunk
    if (this.hostPaused) {
      this.lastSnapshotAt = at;
      return;
    }
    const blocked = this.hostPaused || this.stalled;
    this.hostPaused = false;
    this.stalled = false;
    this.lastSnapshotAt = at;
    if (blocked) {
      // a szünet után a friss állapotból indulunk, ne a régi predikcióból
      this.pred = null;
      this.snapshots = [];
      this.corr = { x: 0, y: 0, h: 0 };
      this.visual.ready = false;
      this.lastTargetTime = 0;
      this.stallGraceUntil = at + 1.0;
      this.handlers.onHostPause?.(false);
    }
    this.snapshots.push({ at, data: snapshot });
    if (this.snapshots.length > SNAP_MAX) this.snapshots.shift();

    if (snapshot.bp.length > 0) {
      this.handlers.onBumps?.(
        snapshot.bp.map((b) => ({ at: { x: b.x, y: b.y }, strength: b.s })),
      );
    }

    const local = this.pred;
    if (!local) return;
    const auth = snapshot.ks.find((k) => k.i === local.id);
    if (!auth) return;
    // a korrekciót a LÁTHATÓ (pred + corr) pozícióhoz mérjük, különben
    // a decay és a rákövetkező lökés 20 Hz-en ide-oda rángatja a kamerát
    const renderedX = local.pos.x + this.corr.x;
    const renderedY = local.pos.y + this.corr.y;
    const dx = auth.x - renderedX;
    const dy = auth.y - renderedY;
    const dist = Math.hypot(dx, dy);
    if (dist > HARD_SNAP) {
      local.pos.x = auth.x;
      local.pos.y = auth.y;
      local.vel.x = auth.vx;
      local.vel.y = auth.vy;
      local.heading = auth.h;
      this.corr = { x: 0, y: 0, h: 0 };
      this.visual.ready = false;
      return;
    }
    // a hibát nem egyből ugrjuk át, hanem ~100 ms alatt simul ki
    this.corr.x = clamp(this.corr.x + dx * 0.45, -120, 120);
    this.corr.y = clamp(this.corr.y + dy * 0.45, -120, 120);
    const dh = wrapAngle(auth.h - (local.heading + this.corr.h));
    this.corr.h = clamp(this.corr.h + dh * 0.45, -0.4, 0.4);
  }

  private interpolate(world: World, now: number): number {
    const snaps = this.snapshots;
    const first = snaps[0];
    const latest = snaps[snaps.length - 1];
    if (!first || !latest) return world.time;

    const serverNow = latest.data.tm + Math.min(now - latest.at, MAX_EXTRAPOLATION);
    let target = Math.max(0, serverNow - INTERP_DELAY);
    // ha a gazda órája lassabban megy (pl. throttolt fül), ne ugorjunk vissza
    if (target < this.lastTargetTime) target = this.lastTargetTime;
    this.lastTargetTime = target;

    let prev = first;
    let next = latest;
    for (let i = 0; i < snaps.length - 1; i++) {
      const a = snaps[i];
      const b = snaps[i + 1];
      if (a && b && a.data.tm <= target && b.data.tm >= target) {
        prev = a;
        next = b;
        break;
      }
    }
    if (target >= latest.data.tm) {
      prev = latest;
      next = latest;
    }

    const span = next.data.tm - prev.data.tm;
    const ratio = span > 0.0001 ? clamp((target - prev.data.tm) / span, 0, 1) : 1;

    const byId = new Map<number, Kart>();
    for (const kart of world.karts) byId.set(kart.id, kart);
    const prevById = new Map<number, KartSnap>();
    for (const kart of prev.data.ks) prevById.set(kart.i, kart);

    for (const snap of next.data.ks) {
      const kart = byId.get(snap.i);
      if (!kart) continue;
      const from = prevById.get(snap.i) ?? snap;
      kart.pos.x = lerp(from.x, snap.x, ratio);
      kart.pos.y = lerp(from.y, snap.y, ratio);
      kart.heading = lerpAngle(from.h, snap.h, ratio);
      kart.vel.x = lerp(from.vx, snap.vx, ratio);
      kart.vel.y = lerp(from.vy, snap.vy, ratio);
      applyDiscrete(kart, snap);
    }

    world.standings = next.data.ord
      .map((id) => byId.get(id))
      .filter((kart): kart is Kart => kart !== undefined);

    const boxes = world.items.boxes;
    for (let i = 0; i < boxes.length && i < next.data.bx.length; i++) {
      const entry = next.data.bx[i];
      const box = boxes[i];
      if (!entry || !box) continue;
      box.active = entry[0] === 1;
      box.respawn = entry[1];
    }
    world.items.hazards = next.data.hz.map(fromWireHazard);
    world.items.projectiles = next.data.pr.map(fromWireProjectile);

    world.phase = next.data.ph;
    world.countdown = next.data.cd;
    world.goTimer = next.data.go;
    return target;
  }

  private predictLocal(world: World, input: import('../core/types').InputState, dt: number): void {
    const kart = world.players[this.localIndex];
    const latest = this.snapshots[this.snapshots.length - 1];
    if (!kart || !latest) return;
    const snap = latest.data.ks.find((k) => k.i === kart.id);
    if (!snap) return;

    if (!this.pred) {
      this.pred = new Kart({
        id: kart.id,
        character: kart.character,
        isPlayer: true,
        playerIndex: kart.playerIndex,
        pos: { x: snap.x, y: snap.y },
        heading: snap.h,
      });
      this.pred.lap = snap.l;
      this.pred.lapStartTime = snap.lt;
      this.pred.vel.x = snap.vx;
      this.pred.vel.y = snap.vy;
    }
    const pred = this.pred;
    // a motorhanghoz és a HUD-hoz a helyi bemenet kell
    kart.setInput(input);

    // a szerver dönt a nem-fizikai állapotról (tárgy, boost, lassítás, pajzs)
    pred.boostTimer = snap.bo;
    pred.boostStrength = 1;
    pred.slowTimer = snap.sl;
    pred.slowFactor = snap.sf;
    pred.spinTimer = snap.sp;
    pred.spinDir = snap.sd || 1;
    pred.shieldTimer = snap.sh;
    pred.abilityCooldown = snap.ac;
    pred.item = snap.it;
    pred.itemRollTimer = snap.rt;
    pred.itemRollTotal = snap.rtt;
    pred.bestLap = snap.bl;
    pred.abilityActive = 0;

    const canDrive = latest.data.ph === 'racing' && snap.f === 0 && snap.o === 0 && snap.rs <= 0;
    if (canDrive) {
      pred.setInput(input);
      pred.update(dt, world.track);
    } else {
      pred.pos.x = snap.x;
      pred.pos.y = snap.y;
      pred.vel.x = snap.vx;
      pred.vel.y = snap.vy;
      pred.heading = snap.h;
      pred.steer = 0;
      this.corr = { x: 0, y: 0, h: 0 };
    }

    // a korrekciót a rendereléskor adjuk hozzá, de a látható pozíciót/irányt
    // külön simítjuk – így a 20 Hz-es korrekciók nem rázzák a kocsit
    const targetX = pred.pos.x + this.corr.x;
    const targetY = pred.pos.y + this.corr.y;
    const targetH = pred.heading + this.corr.h;
    if (!this.visual.ready) {
      this.visual.x = targetX;
      this.visual.y = targetY;
      this.visual.h = targetH;
      this.visual.ready = true;
    } else {
      const dx = targetX - this.visual.x;
      const dy = targetY - this.visual.y;
      if (Math.hypot(dx, dy) > 200) {
        this.visual.x = targetX;
        this.visual.y = targetY;
        this.visual.h = targetH;
      } else {
        const k = Math.min(1, dt * 20);
        this.visual.x += dx * k;
        this.visual.y += dy * k;
        const dh = wrapAngle(targetH - this.visual.h);
        this.visual.h += Math.abs(dh) > 0.5 ? dh : dh * k;
      }
    }
    kart.pos.x = this.visual.x;
    kart.pos.y = this.visual.y;
    kart.heading = this.visual.h;
    kart.vel.x = pred.vel.x;
    kart.vel.y = pred.vel.y;
    kart.steer = pred.steer;
    kart.surface = pred.surface;
    kart.onTrack = pred.onTrack;
    kart.bump = pred.bump;
    kart.driftActive = pred.driftActive;
    kart.driftCharge = pred.driftCharge;
    kart.driftDir = pred.driftDir;
    kart.driftLevel = pred.driftLevel;
    kart.slipAngle = pred.slipAngle;
    kart.hopPhase = pred.hopPhase;
    kart.wallHit = pred.wallHit;
    kart.spinTimer = pred.spinTimer;
    kart.boostTimer = pred.boostTimer;
    kart.slowTimer = pred.slowTimer;
    kart.slowFactor = pred.slowFactor;
    kart.shieldTimer = pred.shieldTimer;
    kart.item = pred.item;
    kart.itemRollTimer = pred.itemRollTimer;
    kart.itemRollTotal = pred.itemRollTotal;
    kart.bestLap = pred.bestLap;

    // a kör/progress a host igazsága (a predikció nem látja az itemeket)
    kart.lap = snap.l;
    kart.progress = snap.p;
    kart.totalProgress = snap.tp;
    kart.lapStartTime = snap.lt;
    kart.finished = snap.f === 1;
    kart.finishTime = snap.ft;

    const decay = Math.exp(-10 * dt);
    this.corr.x *= decay;
    this.corr.y *= decay;
    this.corr.h *= decay;
    if (Math.abs(this.corr.x) < 0.05) this.corr.x = 0;
    if (Math.abs(this.corr.y) < 0.05) this.corr.y = 0;
    if (Math.abs(this.corr.h) < 0.0005) this.corr.h = 0;
  }

  private now(): number {
    return performance.now() / 1000;
  }
}

const clamp = (value: number, lo: number, hi: number): number => (value < lo ? lo : value > hi ? hi : value);

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const wrapAngle = (angle: number): number => {
  let value = angle;
  while (value > Math.PI) value -= Math.PI * 2;
  while (value < -Math.PI) value += Math.PI * 2;
  return value;
};

const lerpAngle = (a: number, b: number, t: number): number => a + wrapAngle(b - a) * t;

export const wireToInput = (wire: InputWire): import('../core/types').InputState => ({
  steer: clamp(wire.s, -1, 1),
  throttle: clamp(wire.t, 0, 1),
  brake: clamp(wire.b, 0, 1),
  drift: wire.d === true,
  useItem: wire.u === true,
  ability: wire.a === true,
});

const applyDiscrete = (kart: Kart, snap: KartSnap): void => {
  kart.lap = snap.l;
  kart.progress = snap.p;
  kart.totalProgress = snap.tp;
  kart.item = snap.it;
  kart.itemRollTimer = snap.rt;
  kart.itemRollTotal = snap.rtt;
  kart.bestLap = snap.bl;
  kart.boostTimer = snap.bo;
  kart.slowTimer = snap.sl;
  kart.slowFactor = snap.sf;
  kart.spinTimer = snap.sp;
  kart.spinDir = snap.sd || 1;
  kart.shieldTimer = snap.sh;
  kart.driftActive = snap.da === 1;
  kart.driftCharge = snap.dc;
  kart.driftDir = snap.dd;
  kart.surface = snap.su;
  kart.abilityCooldown = snap.ac;
  kart.hopPhase = snap.hp;
  kart.out = snap.o === 1;
  kart.respawnTimer = snap.rs;
  kart.finished = snap.f === 1;
  kart.finishTime = snap.ft;
  kart.lapStartTime = snap.lt;
};

export function buildSnapshot(world: World, bumps: BumpWire[]): Snapshot {
  return {
    tm: round(world.time, 3),
    ph: world.phase,
    cd: round(world.countdown, 3),
    go: round(world.goTimer, 3),
    ks: world.karts.map(toKartSnap),
    bx: world.items.boxes.map((box) => [box.active ? 1 : 0, round(box.respawn, 2)]),
    hz: world.items.hazards.map((h) => [
      h.id,
      h.kind,
      round(h.pos.x, 1),
      round(h.pos.y, 1),
      round(h.radius, 1),
      h.ownerId,
      round(h.life, 2),
    ]),
    pr: world.items.projectiles.map((p) => [
      p.id,
      p.kind,
      round(p.pos.x, 1),
      round(p.pos.y, 1),
      round(p.vel.x, 1),
      round(p.vel.y, 1),
      round(p.radius, 1),
      p.ownerId,
      round(p.life, 2),
    ]),
    ord: world.standings.map((k) => k.id),
    ev: [],
    bp: bumps,
  };
}

const toKartSnap = (kart: Kart): KartSnap => ({
  i: kart.id,
  x: round(kart.pos.x, 1),
  y: round(kart.pos.y, 1),
  h: round(kart.heading, 3),
  vx: round(kart.vel.x, 1),
  vy: round(kart.vel.y, 1),
  l: kart.lap,
  p: round(kart.progress, 4),
  tp: round(kart.totalProgress, 4),
  it: kart.item,
  rt: round(kart.itemRollTimer, 2),
  rtt: round(kart.itemRollTotal, 2),
  bl: kart.bestLap === null ? null : round(kart.bestLap, 2),
  bo: round(kart.boostTimer, 2),
  sl: round(kart.slowTimer, 2),
  sf: round(kart.slowFactor, 2),
  sp: round(kart.spinTimer, 2),
  sd: kart.spinDir,
  sh: round(kart.shieldTimer, 2),
  da: kart.driftActive ? 1 : 0,
  dc: round(kart.driftCharge, 2),
  dd: kart.driftDir,
  su: kart.surface,
  ac: round(kart.abilityCooldown, 2),
  hp: round(kart.hopPhase, 2),
  o: kart.out ? 1 : 0,
  rs: round(kart.respawnTimer, 2),
  f: kart.finished ? 1 : 0,
  ft: round(kart.finishTime, 2),
  lt: round(kart.lapStartTime, 2),
});

const round = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const toWireEvent = (event: ItemEvent): ItemEventWire => ({
  k: event.kind,
  i: event.item,
  x: round(event.at.x, 1),
  y: round(event.at.y, 1),
  o: event.ownerId,
  t: event.targetId,
});

const fromWireEvent = (event: ItemEventWire): ItemEvent => ({
  kind: event.k,
  item: event.i,
  at: { x: event.x, y: event.y },
  ownerId: event.o,
  targetId: event.t,
});

const toWireBump = (bump: { at: Vec2; strength: number }): BumpWire => ({
  x: round(bump.at.x, 1),
  y: round(bump.at.y, 1),
  s: round(bump.strength, 2),
});

const fromWireHazard = (h: [number, ItemId, number, number, number, number, number]): Hazard => ({
  id: h[0],
  kind: h[1],
  pos: { x: h[2], y: h[3] },
  radius: h[4],
  ownerId: h[5],
  life: h[6],
  spent: false,
});

const fromWireProjectile = (
  p: [number, ItemId, number, number, number, number, number, number, number],
): Projectile => ({
  id: p[0],
  kind: p[1],
  pos: { x: p[2], y: p[3] },
  vel: { x: p[4], y: p[5] },
  radius: p[6],
  ownerId: p[7],
  life: p[8],
  spent: false,
});
